import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import { formatDate, formatMoneyShort } from '../lib/format'
import type { Client } from '../types'

function today(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Подписка клиента: какой тариф, до какого числа, сколько часов израсходовано.
 *
 * История ведётся записями за периоды, а не одним полем: клиент спрашивает
 * «когда продлевали» и «что было в прошлом году», и по одному полю это не
 * ответить.
 */
export default function SubscriptionTab({
  clientId,
  client,
}: {
  clientId: number
  client?: Client | null
}) {
  const qc = useQueryClient()
  const { can } = useAuth()
  const mayEdit = can('clients.edit')
  const [error, setError] = useState('')
  const [adding, setAdding] = useState(false)

  const list = useQuery({
    queryKey: ['subscriptions', clientId],
    queryFn: () => api.subscriptions(clientId),
  })
  const usage = useQuery({
    queryKey: ['subscription-usage', clientId],
    queryFn: () => api.subscriptionUsage(clientId),
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['subscriptions', clientId] })
    qc.invalidateQueries({ queryKey: ['subscription-usage', clientId] })
  }

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteSubscription(id),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не получилось'),
  })

  const rows = list.data ?? []
  const current = rows.find((s) => s.is_current)
  const u = usage.data

  return (
    <>
      {error && <div className="error">{error}</div>}

      <Responsible clientId={clientId} client={client} mayEdit={mayEdit} onError={setError} />

      {!current && (
        <div className="warn">
          Тариф не назначен — в кабинете клиент увидит «Не назначен». Заведите
          подписку, если клиент обслуживается по договору.
        </div>
      )}

      {current && (
        <div className="pv-cards" style={{ marginBottom: 14 }}>
          <div className="pv-card">
            <div className="pv-label">Тариф</div>
            <div className="pv-value">{current.tariff_name}</div>
            <div className="pv-sub">{formatMoneyShort(Number(current.price))} в месяц</div>
          </div>
          <div className="pv-card">
            <div className="pv-label">Действует</div>
            <div className="pv-value">
              {current.ends_on ? `до ${formatDate(current.ends_on)}` : 'бессрочно'}
            </div>
            <div className="pv-sub">
              {current.days_left === null
                ? `с ${formatDate(current.started_on)}`
                : current.days_left >= 0
                  ? `осталось дней: ${current.days_left}`
                  : 'срок истёк'}
            </div>
          </div>
          {u && u.hours_included && (
            <div className="pv-card">
              <div className="pv-label">Часы по тарифу</div>
              <div className="pv-value">
                {Number(u.hours_used)} / {Number(u.hours_included)}
              </div>
              <div className="pv-sub">
                {Number(u.hours_left) > 0
                  ? `осталось ${Number(u.hours_left)} ч`
                  : 'включённые часы израсходованы'}
              </div>
            </div>
          )}
        </div>
      )}

      {mayEdit && (
        <div className="toolbar">
          <button
            className="btn primary"
            onClick={() => {
              setError('')
              setAdding(true)
            }}
          >
            + Подписка
          </button>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Тариф</th>
              <th>С</th>
              <th>По</th>
              <th className="num">Цена</th>
              <th>Заметка</th>
              {mayEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={mayEdit ? 6 : 5} className="empty">
                  Загрузка…
                </td>
              </tr>
            )}
            {!list.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={mayEdit ? 6 : 5} className="empty">
                  Подписок не было
                </td>
              </tr>
            )}
            {rows.map((s) => (
              <tr key={s.id} style={s.is_current ? undefined : { opacity: 0.65 }}>
                <td>
                  {s.tariff_name}
                  {s.is_current && <span className="group-badge">действует</span>}
                </td>
                <td className="nowrap">{formatDate(s.started_on)}</td>
                <td className="nowrap">{s.ends_on ? formatDate(s.ends_on) : 'бессрочно'}</td>
                <td className="num mono">{formatMoneyShort(Number(s.price))}</td>
                <td>{s.note ?? '—'}</td>
                {mayEdit && (
                  <td className="nowrap">
                    <button
                      className="btn small danger"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (confirm('Удалить запись о подписке?')) remove.mutate(s.id)
                      }}
                    >
                      Удалить
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adding && (
        <AddSubscription
          clientId={clientId}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false)
            refresh()
          }}
        />
      )}
    </>
  )
}

function AddSubscription({
  clientId,
  onClose,
  onDone,
}: {
  clientId: number
  onClose: () => void
  onDone: () => void
}) {
  const [form, setForm] = useState({
    tariff_id: '',
    started_on: today(),
    ends_on: '',
    price_override: '',
    note: '',
  })
  const [error, setError] = useState('')

  const tariffs = useQuery({ queryKey: ['tariffs'], queryFn: () => api.tariffs() })

  const create = useMutation({
    mutationFn: () =>
      api.createSubscription(clientId, {
        tariff_id: Number(form.tariff_id),
        started_on: form.started_on,
        ends_on: form.ends_on || null,
        price_override: form.price_override ? Number(form.price_override) : null,
        note: form.note.trim() || null,
      }),
    onSuccess: onDone,
    onError: (e: any) => setError(e?.message ?? 'Не удалось сохранить'),
  })

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value })

  const chosen = tariffs.data?.find((t) => String(t.id) === form.tariff_id)

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        style={{ maxWidth: 520 }}
        onSubmit={(e) => {
          e.preventDefault()
          setError('')
          create.mutate()
        }}
      >
        <h2>Подписка на тариф</h2>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}
          <div className="grid-2">
            <div className="field full">
              <label htmlFor="sb-tariff">Тариф</label>
              <select id="sb-tariff" value={form.tariff_id} onChange={set('tariff_id')} required>
                <option value="">— выберите —</option>
                {tariffs.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} · {formatMoneyShort(Number(t.price_per_month))}
                    {t.included_hours ? ` · ${Number(t.included_hours)} ч` : ''}
                  </option>
                ))}
              </select>
              {chosen && !chosen.included_hours && (
                <span className="hint-inline">
                  В тарифе нет включённых часов — расход часов клиенту показан не будет
                </span>
              )}
            </div>
            <div className="field">
              <label htmlFor="sb-from">Действует с</label>
              <input
                id="sb-from"
                type="date"
                value={form.started_on}
                onChange={set('started_on')}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="sb-to">По</label>
              <input id="sb-to" type="date" value={form.ends_on} onChange={set('ends_on')} />
              <span className="hint-inline">Пусто — бессрочно</span>
            </div>
            <div className="field">
              <label htmlFor="sb-price">Своя цена, ₽</label>
              <input
                id="sb-price"
                type="number"
                min="0"
                step="0.01"
                value={form.price_override}
                onChange={set('price_override')}
                placeholder="из тарифа"
              />
            </div>
            <div className="field full">
              <label htmlFor="sb-note">Заметка</label>
              <input id="sb-note" value={form.note} onChange={set('note')} />
            </div>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={create.isPending || !form.tariff_id}>
            {create.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  )
}


/**
 * Кто ведёт клиента с нашей стороны.
 *
 * Двое, а не один: вопросы у клиента разного свойства. По договору, счетам и
 * срокам идут к менеджеру, по работе базы и оборудования — к специалисту.
 * С одним «ответственным» половина обращений всё равно пересылалась бы, только
 * вручную и с задержкой.
 *
 * Отсюда же уходят письма о новых обращениях: пока обращение никто не взял,
 * о нём узнают ответственные, а не общий ящик, который читают по остаточному
 * принципу.
 */
function Responsible({
  clientId,
  client,
  mayEdit,
  onError,
}: {
  clientId: number
  client?: Client | null
  mayEdit: boolean
  onError: (e: string) => void
}) {
  const qc = useQueryClient()

  const people = useQuery({ queryKey: ['users'], queryFn: () => api.users() })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.updateClient(clientId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      onError('')
    },
    onError: (e: any) => onError(e?.message ?? 'Не удалось сохранить'),
  })

  const staff = (people.data ?? []).filter((u) => u.is_active)

  return (
    <div className="resp-box">
      <div className="resp-title">Кто ведёт клиента</div>
      <div className="grid-2">
        <div className="field">
          <label htmlFor="resp-manager">Ответственный менеджер</label>
          {mayEdit ? (
            <select
              id="resp-manager"
              value={client?.manager_id ?? ''}
              disabled={save.isPending}
              onChange={(e) => save.mutate({ manager_id: Number(e.target.value) || null })}
            >
              <option value="">— не назначен —</option>
              {staff.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          ) : (
            <div className="resp-value">{client?.manager_name ?? '— не назначен —'}</div>
          )}
        </div>
        <div className="field">
          <label htmlFor="resp-spec">Ответственный специалист</label>
          {mayEdit ? (
            <select
              id="resp-spec"
              value={client?.specialist_id ?? ''}
              disabled={save.isPending}
              onChange={(e) => save.mutate({ specialist_id: Number(e.target.value) || null })}
            >
              <option value="">— не назначен —</option>
              {staff.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          ) : (
            <div className="resp-value">{client?.specialist_name ?? '— не назначен —'}</div>
          )}
        </div>
      </div>
      <span className="hint-inline">
        Пока обращение никто не взял, письмо о нём уходит этим людям. Не назначены —
        уходит на общий адрес.
      </span>
    </div>
  )
}
